import i18n from '@/lang/i18n'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import { AgGridColumnType, AgGridExportType, ExportedColDef } from '@/shared/models/ag-grid.model'
import { ColDef, KeyCreatorParams, SetFilterValuesFuncParams } from '@ag-grid-enterprise/all-modules'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import AttachmentCountRenderer from '@/cellRenderers/common/AttachmentCountRenderer.vue'
import IncidentRenderer from '@/cellRenderers/common/IncidentRenderer.vue'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'
import { isNil } from 'lodash'
import AiAnalysisStatusIconRenderer
    from '@/cellRenderers/common/ai-analysis-status/renderer/ai-analysis-status-icon-renderer.vue'
import CommonSetFilterRenderer from '@/cellRenderers/common/CommonSetFilterRenderer.vue'
import { aiAnalysisState } from '@/api/resources/entityState/ai-analysis-state'
import AiAnalysisStatusIcon from '@/cellRenderers/common/ai-analysis-status/ai-analysis-status-icon.vue'
import { VtGridValues } from '@/grid/vt-grid-values'

const diagnosisCaseGridDetailColumnDefsDefault = (): (ColDef | ExportedColDef)[] => ([
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.samSamplenumber'),
        field: 'samLabel',
        type: AgGridColumnType.TEXT,
        flex: 1,
        minWidth: 150
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.location'),
        field: 'topographicCode.description',
        type: AgGridColumnType.TEXT,
        cellRenderer: ({ data }: ICellRendererParams) => data.topographicCode ? `${data.topographicCode.code} | ${data.topographicCode.description}` : '',
        flex: 1,
        minWidth: 150
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.sampleType'),
        field: 'sampleType.description',
        type: AgGridColumnType.TEXT,
        cellRenderer: ({ data }: ICellRendererParams) => data.sampleType ? `${data.sampleType.code} | ${data.sampleType.description}` : '',
        flex: 1,
        minWidth: 280
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.procedure'),
        field: 'procedure.description',
        type: AgGridColumnType.TEXT,
        cellRenderer: ({ data }: ICellRendererParams) => data.procedure ? `${data.procedure.code} | ${data.procedure.description}` : '',
        flex: 1,
        minWidth: 280
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.mostLikelyDiagnosis'),
        field: 'mostLikelyMorphologicCode.mlmDescription',
        type: AgGridColumnType.TEXT,
        cellRenderer: ({ data }: ICellRendererParams) => data.mostLikelyMorphologicCode ? `${data.mostLikelyMorphologicCode.code} | ${data.mostLikelyMorphologicCode.description}` : ''
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.sampleProcess'),
        field: 'sampleProcess.name',
        type: AgGridColumnType.TEXT
    },
    {
        headerName: i18n.tc('global.laboratory'),
        field: 'currentLaboratory.labName',
        type: AgGridColumnType.TEXT
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.grossingDate'),
        field: 'lastGrossingDate',
        type: 'dateTime'
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.globalDelay'),
        field: 'globalDelay',
        type: AgGridColumnType.TEXT,
        width: 110,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: ICellRendererParams) => {
            const delay = data?.globalDelay
            if (!isNil(delay)) {
                const delayAbs = Math.abs(delay)
                return i18n.tc(
                    `diagnosis.grid.caseGrid.delay.${delay > 0 ? 'positive' : 'negative'}`,
                    delayAbs > 1 ? 2 : 1,
                    { delay: delayAbs })
            }
            return ''
        }
    },
    {
        headerName: i18n.tc('diagnosis.grid.caseGrid.diagnosticDelay'),
        field: 'diagnosticDelay',
        type: AgGridColumnType.TEXT,
        width: 110,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: ICellRendererParams) => {
            const delay = data?.diagnosticDelay
            if (!isNil(delay)) {
                const delayAbs = Math.abs(delay)
                return i18n.tc(
                    `diagnosis.grid.caseGrid.delay.${delay > 0 ? 'positive' : 'negative'}`,
                    delayAbs > 1 ? 2 : 1,
                    { delay: delayAbs })
            }
            return ''
        }
    },
    {
        headerName: i18n.tc('grid.hasAlarms'),
        headerTooltip: i18n.tc('grid.hasAlarms'),
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-exclamation-triangle')
        },
        field: 'incidentsCount',
        type: AgGridColumnType.INCIDENT,
        cellRendererFramework: IncidentRenderer,
        sortable: false,
        filter: false,
        hide: false,
        maxWidth: 80
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.attachments'),
        headerTooltip: i18n.tc('diagnosis.grid.sampleGrid.attachments'),
        headerComponentParams: {
            template: buildIconHeaderTemplate('far fa-paperclip')
        },
        field: 'attachmentsCount',
        type: AgGridColumnType.ATTACHMENT_COUNT,
        cellRendererFramework: AttachmentCountRenderer,
        sortable: false,
        filter: false,
        hide: false,
        maxWidth: 80
    },
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.specialist'),
        field: 'specialist.fullName',
        type: AgGridColumnType.TEXT
    },
    {
        headerName: i18n.tc('diagnosis.aiInformation'),
        headerComponentParams: {
            template: buildIconHeaderTemplate('fas fa-brain')
        },
        headerTooltip: i18n.tc('diagnosis.aiInformation'),
        field: 'analysisStatusAI',
        colId: 'analysisStatusAI.code',
        keyCreator: (params: KeyCreatorParams): string => params.value.code,
        exportType: AgGridExportType.TEXT,
        maxWidth: 80,
        cellRendererFramework: AiAnalysisStatusIconRenderer,
        filterParams: {
            suppressMiniFilter: true,
            cellRendererFramework: CommonSetFilterRenderer,
            cellRendererParams: {
                states: aiAnalysisState.states,
                component: AiAnalysisStatusIcon
            },
            values: (params: SetFilterValuesFuncParams): void => VtGridValues.entityStatusStateValues(params, aiAnalysisState.states),
            debounceMs: 1000
        }
    }
    // TODO: Informe provisional oculto hasta después de la LITE, tiene que definirse mejor
    /*
    {
        headerName: i18n.tc('diagnosis.grid.sampleGrid.provisionalReport'),
        field: 'sampleProces.sapDescription',
        type: AgGridColumnType.TEXT,
        suppressSizeToFit: true,
        width: 110
    }
    */
])

export const diagnosisCaseGridDetailColumnDefs = (): (ColDef | ExportedColDef)[] => {
    return ModuleOperationGuard.canAccessBilling
        ? diagnosisCaseGridDetailColumnDefsDefault().concat([{
            type: AgGridColumnType.HAS_UNAUTHORIZED_BILLABLE_CONCEPTS,
            exportType: AgGridExportType.BOOLEAN,
            field: 'hasUnauthorizedBillableConcepts',
            flex: 1,
            minWidth: 55
        }])
        : diagnosisCaseGridDetailColumnDefsDefault()
}
